import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Lock, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Eye,
  EyeOff,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiErrors';

const AlterarSenha = () => {
  const { api, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Show/hide password toggles
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (!formData.senhaAtual) {
      setError('Digite sua senha atual');
      return;
    }

    if (!formData.novaSenha) {
      setError('Digite a nova senha');
      return;
    }

    if (formData.novaSenha.length < 10 || new TextEncoder().encode(formData.novaSenha).length > 72) {
      setError('A senha deve ter no mínimo 10 caracteres e no máximo 72 bytes');
      return;
    }

    if (formData.novaSenha !== formData.confirmarSenha) {
      setError('Nova senha e confirmação não conferem.');
      return;
    }

    if (formData.senhaAtual === formData.novaSenha) {
      setError('A nova senha deve ser diferente da atual');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/change-password', {
        senha_atual: formData.senhaAtual,
        nova_senha: formData.novaSenha,
        confirmar_senha: formData.confirmarSenha
      });
      
      setSuccess(true);
      toast.success('Senha alterada. Faça login novamente com a nova senha.');
      await logout();
      navigate('/login', { replace: true });
      
      // Clear form
      setFormData({
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: ''
      });
      
    } catch (err) {
      const message = apiErrorMessage(err, 'Erro ao alterar senha');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto space-y-6" data-testid="alterar-senha-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            data-testid="change-password-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
              Alterar Senha
            </h1>
            <p className="text-muted-foreground">
              Atualize sua senha de acesso
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-center">Alterar Senha</CardTitle>
            <CardDescription className="text-center">
              Para sua segurança, digite sua senha atual e depois a nova senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20" data-testid="change-password-success">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700 dark:text-green-400">
                    Senha alterada com sucesso! Sua nova senha já está ativa.
                  </AlertDescription>
                </Alert>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => setSuccess(false)}
                  data-testid="change-password-again"
                >
                  Alterar novamente
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="change-password-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Current Password */}
                <div className="space-y-2">
                  <Label htmlFor="senhaAtual">Senha Atual</Label>
                  <div className="relative">
                    <Input
                      id="senhaAtual"
                      data-testid="change-password-current"
                      autoComplete="current-password"
                      type={showSenhaAtual ? "text" : "password"}
                      placeholder="Digite sua senha atual"
                      value={formData.senhaAtual}
                      onChange={(e) => handleChange('senhaAtual', e.target.value)}
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                      data-testid="change-password-toggle-current"
                      aria-label="Mostrar ou ocultar senha atual"
                    >
                      {showSenhaAtual ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-4">Nova Senha</p>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="novaSenha">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="novaSenha"
                      data-testid="change-password-new"
                      autoComplete="new-password"
                      maxLength={72}
                      type={showNovaSenha ? "text" : "password"}
                      placeholder="Mínimo 10 caracteres"
                      value={formData.novaSenha}
                      onChange={(e) => handleChange('novaSenha', e.target.value)}
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowNovaSenha(!showNovaSenha)}
                      data-testid="change-password-toggle-new"
                      aria-label="Mostrar ou ocultar nova senha"
                    >
                      {showNovaSenha ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  {formData.novaSenha && formData.novaSenha.length < 10 && (
                    <p className="text-xs text-red-500">A senha deve ter pelo menos 10 caracteres</p>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmarSenha"
                      data-testid="change-password-confirm"
                      autoComplete="new-password"
                      type={showConfirmarSenha ? "text" : "password"}
                      placeholder="Repita a nova senha"
                      value={formData.confirmarSenha}
                      onChange={(e) => handleChange('confirmarSenha', e.target.value)}
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                      data-testid="change-password-toggle-confirm"
                      aria-label="Mostrar ou ocultar confirmação de senha"
                    >
                      {showConfirmarSenha ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  {formData.confirmarSenha && formData.novaSenha !== formData.confirmarSenha && (
                    <p className="text-xs text-red-500">Nova senha e confirmação não conferem.</p>
                  )}
                  {formData.confirmarSenha && formData.novaSenha === formData.confirmarSenha && formData.novaSenha.length >= 10 && (
                    <p className="text-xs text-green-500 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Senhas coincidem
                    </p>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  data-testid="change-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Alterando...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 mr-2" />
                      Alterar Senha
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AlterarSenha;
